import { CalculatorOutlined } from '@ant-design/icons';
import { useGetFrontRichiesteIdPagamenti } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { CurrencyRender } from '@framework/ui/components/currency/CurrencyRender.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { SectionTitle } from '@framework/ui/components/summary/SectionTitle.tsx';
import { Flex, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getValidPayments } from '../../../../payments/utils/getValidPayments.ts';
import { ExtendedPaymentsTable } from './ExtendedPaymentsTable.tsx';

export interface ISummaryPaymentsSectionProps {
  submission: RichiestaDTO;
  /** Se true, nasconde le azioni sui pagamenti (es. consultazione dal Backoffice) */
  readonly?: boolean;
}

/**
 * Sezione Pagamenti nel riepilogo esteso.
 * Mostra i pacchetti di pagamento con dettaglio oneri espandibile.
 */
export function SummaryPaymentsSection(props: ISummaryPaymentsSectionProps) {
  const { submission, readonly } = props;
  const { t } = useTranslation();

  const { data, isLoading } = useGetFrontRichiesteIdPagamenti(
    submission.id ?? '<unknown>',
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submission.id),
      },
    },
  );

  const payments = useMemo(() => getValidPayments(data), [data]);

  const total = useMemo(
    () => payments.reduce((sum, payment) => sum + (payment.valore ?? 0), 0),
    [payments],
  );

  if (isLoading) {
    return <LoaderSpin transparent />;
  }

  if (payments.length === 0) {
    return null;
  }

  return (
    <div>
      <Flex justify="space-between" align="center">
        <SectionTitle
          icon={<CalculatorOutlined />}
          title={t('summary-payments.pagamenti', 'Pagamenti')}
        />
        <Typography.Text strong style={{ fontSize: '1.1rem' }}>
          {t('summary-payments.totale', 'Totale')}{' '}
          <CurrencyRender value={total} />
        </Typography.Text>
      </Flex>
      <VerticalSpace />

      <ExtendedPaymentsTable
        payments={payments}
        submission={submission}
        readonly={readonly}
      />
    </div>
  );
}
